import {Helmet} from '@dr.pogodin/react-helmet';
import {skipToken} from '@reduxjs/toolkit/query';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {useParams} from 'react-router-dom';

import {isNil, isNotNil} from 'ramda';

import {useGetUserQuery} from '@dms/api/customer';
import {useGetRmaRequestQuery} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {
  DetailTemplate,
  DetailTemplateHeader,
  EntityHeaderFlag,
  getUserName,
  NavigationItem,
} from '@dms/shared';

import {composePath, EMPTY_PLACEHOLDER, parseDate} from 'shared/web';

import {RMA_REQUEST_STATUS} from '../../constants/RMA_REQUEST_STATUS';
import {Overview} from './(sections)/Overview/Overview';

export function RmaRequestDetail() {
  const {id: rmaRequestId} = useParams<{id: string}>();

  const dateTimeFormatter = useDateTimeFormatter();

  const rmaRequestQuery = useGetRmaRequestQuery(
    isNotNil(rmaRequestId) ? {rmaRequestId} : skipToken
  );

  const createdBy = rmaRequestQuery.data?.createdBy;

  const createdByUserQuery = useGetUserQuery(isNotNil(createdBy) ? {id: createdBy} : skipToken);

  const createdAt = rmaRequestQuery.data?.createdAt;

  const createdAtLabel = isNil(createdAt)
    ? EMPTY_PLACEHOLDER
    : dateTimeFormatter('dateShort', parseDate(createdAt));

  const headerFlags = match(rmaRequestQuery.data?.status)
    .returnType<EntityHeaderFlag[]>()
    .with(RMA_REQUEST_STATUS.PENDING, () => [
      {colorScheme: 'neutral', label: i18n.t('general.labels.pending')},
    ])
    .with(RMA_REQUEST_STATUS.REQUESTED, () => [
      {colorScheme: 'yellow', label: i18n.t('entity.tireSet.labels.requested')},
    ])
    .with(RMA_REQUEST_STATUS.COMPLETED, () => [
      {colorScheme: 'green', label: i18n.t('entity.warehouse.labels.completed')},
    ])
    .otherwise(() => []);

  const header: DetailTemplateHeader = {
    icon: 'action/return_item',
    title: `${i18n.t('entity.warehouse.labels.rmaRequest')} ${rmaRequestQuery.data?.number ?? ''}`,
    flags: headerFlags,
    parameters: [
      `${i18n.t('general.labels.createdAt')}: ${createdAtLabel}`,
      `${i18n.t('general.labels.createdBy')}: ${getUserName(createdByUserQuery.data) ?? EMPTY_PLACEHOLDER}`,
    ],
  };

  const navigation: NavigationItem[] = [
    {
      id: 'rma-request-detail-overview',
      label: i18n.t('page.warehouse.labels.overview'),
      href: composePath(warehouseRoutes.rmaRequestDetailOverview, {params: {id: rmaRequestId}}),
      content: <Overview rmaRequestId={rmaRequestId ?? ''} />,
    },
  ];

  return (
    <>
      <Helmet title={i18n.t('entity.warehouse.labels.rmaRequest')} />
      <DetailTemplate
        isLoading={rmaRequestQuery.isLoading}
        isError={rmaRequestQuery.isError}
        header={header}
        navigation={navigation}
        data-testid={testIds.warehouse.rmaRequestDetailOverview('page')}
      />
    </>
  );
}
